<template>
  <div class="typo-vorschau">
    <b-row class="mb-3">
      <b-col cols="12" offset-sm="1" sm="3">
        <label>{{ t('rollenLabel') }}</label>
      </b-col>
      <b-col cols="12" sm="7">
        <b-form-text>{{ t(zeigeSeite ? 'rollenHint' : 'rollenHintListe') }}</b-form-text>
      </b-col>
    </b-row>
    <b-row class="mb-4">
      <b-col cols="12">
        <div class="tv-layout" :class="{ 'tv-layout-liste': !zeigeSeite }">
          <div v-if="zeigeSeite" class="tv-vorschau-spalte">
            <typo-buehne :geraet.sync="geraet">
              <template #default="{ geraet: g }">
                <div class="tv-innen" :class="`tv-innen-${g}`">
                  <template v-for="role in seite">
                    <div v-if="role === 'caption'" :key="`${role}-bild`" class="tv-bild" />
                    <button
                      :key="role"
                      type="button"
                      class="tv-el"
                      :class="{ aktiv: gewaehlt === role, 'tv-el-luft': ['h2', 'h3', 'button'].includes(role) }"
                      :title="t(`rolle.${role}`)"
                      :data-test="`typo-el-${role}`"
                      @click="gewaehlt = role"
                    >
                      <span :class="`tv-text-${role}`" :style="stil(role)">{{ t(`muster.${role}`) }}</span>
                    </button>
                  </template>
                </div>
              </template>
            </typo-buehne>
          </div>
          <!-- Brand kit (Rev. 2 #5): every role as a line in its own style, no page. -->
          <div v-else class="tv-liste" data-test="typo-liste">
            <button
              v-for="role in rollen"
              :key="role"
              type="button"
              class="tv-zeile"
              :class="{ aktiv: gewaehlt === role }"
              :aria-pressed="String(gewaehlt === role)"
              :data-test="`typo-liste-${role}`"
              @click="gewaehlt = role"
            >
              <span class="tv-zeile-muster" :style="listenStil(role)">{{ t(`rolle.${role}`) }}</span>
              <span class="tv-zeile-werte">{{ listenWerte(role) }}</span>
            </button>
          </div>

          <div :key="`${gewaehlt}-${renderKey}`" class="tv-inspektor" :data-test="`typo-inspektor-${gewaehlt}`">
            <div>
              <div class="tv-klein">{{ t('ausgewaehlt') }}</div>
              <div class="tv-name">{{ t(`rolle.${gewaehlt}`) }}</div>
              <div class="tv-wo">{{ t(`wo.${gewaehlt}`) }}</div>
            </div>

            <div class="tv-feld">
              <label :for="id('schrift')">{{ t('schrift') }}</label>
              <b-form-select
                :id="id('schrift')"
                :data-test="`typo-schrift-${gewaehlt}`"
                :value="rollenStilWert(typo, gewaehlt, 'schrift')"
                :options="schriftOptionen(gewaehlt)"
                @change="setzeSchrift(gewaehlt, $event)"
              />
            </div>

            <div class="tv-paar">
              <div v-for="feld in ['min', 'max']" :key="feld" class="tv-feld">
                <label :for="id(feld)">{{ t(feld === 'min' ? 'feld.handy' : 'feld.desktop') }}</label>
                <b-form-input
                  :id="id(feld)"
                  type="text"
                  inputmode="decimal"
                  :data-test="`typo-${feld}`"
                  :class="{ 'tv-standard': istStandard(gewaehlt, feld) }"
                  :value="feldWert(gewaehlt, feld)"
                  @change="setzeGroesse(gewaehlt, feld, $event)"
                />
              </div>
            </div>

            <div class="tv-feld">
              <label :for="id('staerke')">{{ t('stil.staerke') }}</label>
              <b-form-select
                :id="id('staerke')"
                :data-test="`typo-stil-${gewaehlt}-staerke`"
                :value="rollenStilWert(typo, gewaehlt, 'staerke')"
                :options="staerkeOptionenFuer(gewaehlt)"
                @change="setzeStil(gewaehlt, 'staerke', $event)"
              />
              <small
                v-if="staerkeFehlt(gewaehlt)"
                class="text-warning"
                :data-test="`typo-warnung-${gewaehlt}`"
              >{{ t('staerkeFehlt') }}</small>
            </div>

            <div v-if="mehr" class="tv-paar" data-test="typo-mehr">
              <div class="tv-feld">
                <label :for="id('lh')">{{ t('feld.zeilenhoehe') }}</label>
                <b-form-input
                  :id="id('lh')"
                  type="text"
                  inputmode="decimal"
                  :class="{ 'tv-standard': istStandard(gewaehlt, 'lineHeight') }"
                  :value="feldWert(gewaehlt, 'lineHeight')"
                  @change="setzeZeilenhoehe(gewaehlt, $event)"
                />
              </div>
              <div class="tv-feld">
                <label :for="id('ls')">{{ t('feld.laufweite') }}</label>
                <b-form-input
                  :id="id('ls')"
                  type="text"
                  inputmode="decimal"
                  :class="{ 'tv-standard': istStandard(gewaehlt, 'ls') }"
                  :value="feldWert(gewaehlt, 'ls')"
                  :title="t('laufweiteHint')"
                  @change="setzeLaufweite(gewaehlt, $event)"
                />
              </div>
              <template v-if="gewaehlt !== 'button'">
                <div v-for="feld in ['kursiv', 'gross']" :key="feld" class="tv-feld">
                  <label :for="id(feld)">{{ t(`feld.${feld}`) }}</label>
                  <b-form-select
                    :id="id(feld)"
                    :data-test="`typo-stil-${gewaehlt}-${feld}`"
                    :value="rollenStilWert(typo, gewaehlt, feld)"
                    :options="jaNeinOptionen"
                    @change="setzeStil(gewaehlt, feld, $event)"
                  />
                </div>
              </template>
              <b-form-text class="tv-ganz">{{ t('laufweiteHint') }}</b-form-text>
            </div>

            <div class="tv-leiste">
              <button type="button" class="tv-link" data-test="typo-mehr-knopf" @click="mehr = !mehr">
                {{ t(mehr ? 'weniger' : 'mehrOptionen') }}
              </button>
              <button type="button" class="tv-link tv-link-leise" data-test="typo-zuruecksetzen" @click="zuruecksetzen(gewaehlt)">
                {{ t('zuruecksetzen') }}
              </button>
            </div>

            <div class="tv-leiste tv-angepasst" data-test="typo-angepasst">
              <div class="tv-klein">{{ t('angepasst') }}</div>
              <div v-if="angepasst.length">
                <button
                  v-for="(role, i) in angepasst"
                  :key="role"
                  type="button"
                  class="tv-link"
                  @click="gewaehlt = role"
                >{{ t(`rolle.${role}`) }}{{ angepasst.length - 1 > i ? ',' : '' }}</button>
              </div>
              <div v-else class="tv-wo">{{ t('nichtsAngepasst') }}</div>
            </div>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
// The CI typography as a small magazine page (spec 2026-09-29 §1d, prototype
// B): a click on a text picks its role, the panel next to it holds only that
// role's settings. Shared by project and folder settings; the parent owns
// saving and listens to `change`, `schrift` asks it to load a font.
import { TYPOGRAPHY_ROLES, TYPE_CURVE } from '@/editor-v2/utils/ciTokens';
import {
  groesseAnzeigen, groesseLesen, zeilenhoeheLesen, laufweiteLesen, overrideSetzen,
  hochgeladeneStaerken, staerkeOptionen, schriftFuerRolle, themeStaerke, vorschauStil,
  rolleZuruecksetzen,
} from './typoTabelle';
import { rollenStilSetzen, rollenStilWert } from './rollenStil';
import TypoBuehne from './TypoBuehne.vue';

// Every role once, in reading order; the image sits above the caption.
const SEITE = Object.freeze(['display', 'h1', 'lead', 'h2', 'body', 'caption', 'h3', 'h4', 'h5', 'h6', 'button']);
const TEXT_ROLES = ['lead', 'body', 'caption', 'button'];

export default {
  name: 'TypoVorschau',
  components: { TypoBuehne },
  props: {
    // false (brand kit): a list of the roles instead of the page (Rev. 2 #5).
    zeigeSeite: { type: Boolean, default: true },
    // The chosen role (.sync); without it the component keeps its own.
    rolle: { type: String, default: null },
    // {[family]: number[]} uploaded weights per custom font; other fonts are absent.
    customFontWeights: { type: Object, default: () => ({}) },
    // [{value, text}] or plain names: fonts a role may get as its own font (the parent's font list).
    schriften: { type: Array, default: () => [] },
    typo: { type: Object, required: true },
  },
  data: () => ({
    eigeneRolle: 'h1',
    geraet: 'desktop',
    mehr: false,
    // Bumped on invalid input so one-way :value fields re-render the stored value.
    renderKey: 0,
    seite: SEITE,
    rollen: TYPOGRAPHY_ROLES,
  }),
  computed: {
    gewaehlt: {
      get() { return this.rolle || this.eigeneRolle; },
      set(role) {
        this.eigeneRolle = role;
        this.$emit('update:rolle', role);
      },
    },
    jaNeinOptionen() {
      return [
        { value: '', text: this.t('stil.theme') },
        { value: 'ja', text: this.t('stil.ja') },
        { value: 'nein', text: this.t('stil.nein') },
      ];
    },
    angepasst() {
      const { overrides, roleStyles } = this.typo;
      return TYPOGRAPHY_ROLES.filter((r) => (overrides && overrides[r]) || (roleStyles && roleStyles[r]));
    },
  },
  methods: {
    groesseAnzeigen,
    rollenStilWert,
    t(key) {
      return this.$t(`settings.project.corporateIdentity.typo.${key}`);
    },
    id(feld) {
      return `typo-${this._uid}-${feld}`;
    },
    stil(role) {
      return vorschauStil(role, this.typo, this.geraet);
    },
    // A list line: the role in its desktop style, at most 24px.
    listenStil(role) {
      const st = vorschauStil(role, this.typo, 'desktop');
      return { ...st, fontSize: `${Math.min(24, parseFloat(st.fontSize))}px`, lineHeight: '1.2' };
    },
    listenWerte(role) {
      const staerke = rollenStilWert(this.typo, role, 'staerke') || themeStaerke(role);
      const eigen = this.angepasst.includes(role) ? ` · ${this.t('eigen')}` : '';
      return `${this.feldWert(role, 'min')} / ${this.feldWert(role, 'max')} px · ${this.t(`staerkeName.${staerke}`)}${eigen}`;
    },
    istStandard(role, feld) {
      return typeof this.wert(role, feld) !== 'number';
    },
    // The value in effect: the role's own one, else the default curve's.
    feldWert(role, feld) {
      const own = this.wert(role, feld);
      const kurve = TYPE_CURVE[role];
      if (feld === 'min' || feld === 'max') return groesseAnzeigen(typeof own === 'number' ? own : kurve[feld]);
      if (feld === 'lineHeight') return String(typeof own === 'number' ? own : kurve.lh);
      return String(typeof own === 'number' ? own : (kurve.ls || 0));
    },
    wert(role, feld) {
      const o = this.typo.overrides && this.typo.overrides[role];
      return o ? o[feld] : undefined;
    },
    speichern(role, feld, wert) {
      // Only invalid input resets the field; a clamped value equal to the stored one may keep the typed text.
      if (wert === undefined) {
        this.renderKey += 1;
        return;
      }
      // An emptied field falls back to the default, which it then shows again.
      if (wert === null) this.renderKey += 1;
      overrideSetzen(this.typo, role, feld, wert);
      this.$emit('change');
    },
    setzeGroesse(role, feld, text) {
      this.speichern(role, feld, groesseLesen(text, 'px'));
    },
    setzeZeilenhoehe(role, text) {
      const max = this.wert(role, 'max');
      this.speichern(role, 'lineHeight', zeilenhoeheLesen(text, typeof max === 'number' ? max : TYPE_CURVE[role].max));
    },
    setzeLaufweite(role, text) {
      this.speichern(role, 'ls', laufweiteLesen(text));
    },
    setzeStil(role, feld, wahl) {
      rollenStilSetzen(this.typo, role, feld, wahl);
      this.$emit('change');
      // A new weight needs its cut loaded (spec 1d, correction 3).
      if (feld === 'staerke') this.$emit('schrift', schriftFuerRolle(role, this.typo));
    },
    setzeSchrift(role, name) {
      rollenStilSetzen(this.typo, role, 'schrift', name);
      this.$emit('change');
      if (name) this.$emit('schrift', name);
    },
    zuruecksetzen(role) {
      rolleZuruecksetzen(this.typo, role);
      this.renderKey += 1;
      this.$emit('change');
    },
    schriftOptionen(role) {
      const standard = this.t(TEXT_ROLES.includes(role) ? 'schriftStandardText' : 'schriftStandardUeberschrift');
      // Accepts plain names or {value, text}.
      const liste = this.schriften.filter(Boolean).map((o) => (typeof o === 'string' ? { value: o, text: o } : o)).filter((o) => o.value !== '');
      return [{ value: '', text: standard }, ...liste];
    },
    staerkeOptionenFuer(role) {
      return staerkeOptionen(hochgeladeneStaerken(role, this.typo, this.customFontWeights), (k) => {
        if (k === 'theme') return `${this.t('stil.theme')} (${themeStaerke(role)})`;
        if (k === 'missing') return this.t('nichtHochgeladen');
        return this.t(`staerkeName.${k}`);
      });
    },
    staerkeFehlt(role) {
      const weights = hochgeladeneStaerken(role, this.typo, this.customFontWeights);
      const gewaehlt = rollenStilWert(this.typo, role, 'staerke');
      return !!weights && gewaehlt !== '' && !weights.includes(gewaehlt);
    },
  },
};
</script>

<style scoped>
.tv-inspektor .tv-standard { color: #b9bbbf; }
/* width 0 + min-width: the preview never widens the settings column (the
   tab content is a flex item and would drop below the tab list). The -15px
   margin + 15px padding take in the negative margins of the bootstrap rows
   inside, so scrollWidth never exceeds the width. */
.typo-vorschau {
  width: 0; min-width: calc(100% + 30px); margin: 0 -15px; padding: 0 15px; box-sizing: border-box;
}
.tv-layout { display: flex; flex-wrap: wrap; gap: 24px; align-items: flex-start; }
.tv-layout > * { min-width: 0; }
/* 316 + 24 + 300 = 640: only below 640px the panel wraps under the preview. */
.tv-vorschau-spalte { flex: 0 1 480px; display: flex; flex-direction: column; gap: 12px; }
.tv-inspektor {
  flex: 0 0 300px; max-width: 100%; box-sizing: border-box; padding: 24px;
  position: sticky; top: 80px; align-self: flex-start;
  background: #53575b; border-radius: 12px;
  display: flex; flex-direction: column; gap: 18px;
}

.tv-innen { padding: 48px 56px; display: flex; flex-direction: column; gap: 14px; }
.tv-innen-handy { padding: 28px 22px; }
.tv-el {
  all: unset; display: block; box-sizing: border-box; width: 100%; cursor: pointer;
  border-radius: 4px; outline-offset: 4px;
}
.tv-el:hover { outline: calc(1px / var(--tv-zoom)) dashed #8a8d92; }
.tv-el.aktiv, .tv-el:focus-visible { outline: calc(2px / var(--tv-zoom)) solid #2f8f5b; }
.tv-el-luft { margin-top: 14px; }
.tv-el > span { display: block; color: #1f2226; overflow-wrap: break-word; }
.tv-el > .tv-text-caption { color: #5b5f66; }
.tv-el > .tv-text-button { display: inline-block; background: #1f5fbf; color: #fff; padding: 12px 22px; border-radius: 6px; }
.tv-bild { height: 180px; background: #d9d6cf; border-radius: 6px; margin-top: 8px; }
.tv-innen-handy .tv-bild { height: 140px; }

/* List mode (brand kit): list above, the role's fields below. */
.tv-layout-liste { flex-direction: column; align-items: stretch; }
.tv-layout-liste .tv-inspektor { flex: 1 1 auto; position: static; }
.tv-liste { display: flex; flex-direction: column; gap: 2px; }
.tv-zeile {
  all: unset; box-sizing: border-box; cursor: pointer; display: flex; justify-content: space-between;
  align-items: baseline; gap: 12px; padding: 8px 10px; border-radius: 6px;
}
.tv-zeile:hover { background: #4a4e52; }
.tv-zeile.aktiv { background: #5b6064; }
.tv-zeile:focus-visible { outline: 2px solid #51d182; }
.tv-zeile-muster { color: #fff; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tv-zeile-werte { flex-shrink: 0; font-size: 13px; color: #b9bbbf; }

.tv-klein { font-size: 12px; letter-spacing: 1px; font-weight: 700; text-transform: uppercase; color: #b9bbbf; }
.tv-name { font-size: 26px; font-weight: 700; color: #fff; line-height: 1.2; }
.tv-wo { font-size: 14px; color: #abaaaa; }
.tv-feld { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
.tv-feld label { margin: 0; }
.tv-paar { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
.tv-ganz { grid-column: 1 / -1; margin-top: 0; }
.tv-leiste { display: flex; justify-content: space-between; gap: 16px; border-top: 1px solid #5b6064; padding-top: 16px; }
.tv-angepasst { flex-direction: column; gap: 8px; }
.tv-link { all: unset; cursor: pointer; color: #51d182; font-size: 14px; margin-right: 6px; }
.tv-link:hover, .tv-link:focus-visible { text-decoration: underline; }
.tv-link-leise { color: #d9dadc; margin-right: 0; }
</style>
